/**
 * Browser-environment probes. Everything here is best-effort and never authoritative.
 */

export function webglSupported(): boolean {
  if (typeof document === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    if (!gl) return false;
    const ext = gl.getExtension("WEBGL_lose_context");
    ext?.loseContext();
    return true;
  } catch {
    return false;
  }
}

export function no3dRequested(search: string = typeof window === "undefined" ? "" : window.location.search): boolean {
  const params = new URLSearchParams(search);
  const v = params.get("no3d");
  return v === "1" || v === "true";
}

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function onReducedMotionChange(cb: (reduced: boolean) => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
  const handler = (e: MediaQueryListEvent) => cb(e.matches);
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}

export function isTouchDevice(): boolean {
  if (typeof window === "undefined") return false;
  const coarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  return coarse || navigator.maxTouchPoints > 0;
}

export function isSmallViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 900;
}

/** Random id for client-originated analytics candidates (8..64 chars per the contract). */
export function clientEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `ce_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

export function clientMessageId(): string {
  return clientEventId();
}
